import React, { useMemo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { MEMBER_PROFILES } from '../data/member-profiles';
import { getMemberPhotoStyle, formatMemberLocation, getTranslatedField } from '../utils/member-helpers';
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
export function OurPeopleStories() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, {
    once: true,
    margin: '-50px'
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  // Shuffle once per mount, then duplicate for seamless infinite loop
  const shuffled = useMemo(() => shuffleArray(MEMBER_PROFILES), []);
  const doubled = [...shuffled, ...shuffled];
  return <section id="our-stories" ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden" data-id="element-3422" data-mp="1dzvm23if2go6">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-3423" data-mp="1nrt51wfo2cxg">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-haa-skyblue/5 rounded-full blur-3xl" data-id="element-3424" data-mp="1so9seof1kn9h" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 bg-haa-midblue/5 rounded-full blur-3xl" data-id="element-3425" data-mp="1wsr92eq3ukg1" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3426" data-mp="21qiyn23a1uh2">
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex flex-col items-center" data-id="element-3427" data-mp="1ot9p41agwgd">
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12 max-w-3xl" data-id="element-3428" data-mp="bf4cuod5f1m7">
            <h2 className="font-bebas text-5xl md:text-6xl text-haa-navy tracking-wider mb-4" data-id="element-3429" data-mp="lbz8mzi1enws">
              {isEs ? 'Nuestra Gente, Nuestras Historias' : 'Our People Our Stories'}
            </h2>
            <p className="font-nunito text-lg text-gray-600 mb-5" data-id="element-3430" data-mp="ucxelnc1d5i0">
              {isEs ? 'Conoce a los hondureño-americanos que están generando un impacto en todo Estados Unidos, liderando en los negocios, el servicio público y la comunidad.' : 'Meet the Honduran-Americans making an impact across the United States, leading in business, public service, and community.'}
            </p>
            <motion.a href="#/members" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 bg-haa-navy text-white font-nunito font-bold text-sm px-7 py-3 rounded-full shadow-md hover:bg-haa-blue transition-colors" data-id="element-3431" data-mp="16o7xwvu8mitx">
              {isEs ? 'Conoce a Nuestra Gente' : 'Meet Our Gente'}
              <ArrowRightIcon className="w-4 h-4" data-id="element-3432" data-mp="sv8jyya3swoh" />
            </motion.a>
          </motion.div>

          {/* Auto-scrolling row */}
          <motion.div variants={itemVariants} className="w-full" data-id="element-3433" data-mp="ewsa4ygy2q24">
            <div className="relative w-full overflow-hidden" style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)'
          }} data-id="element-3434" data-mp="ix2fqa0tregg">
              <div className="flex member-scroll" style={{
              width: 'max-content'
            }} onMouseEnter={e => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.animationPlayState = 'paused';
            }} onMouseLeave={e => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.animationPlayState = 'running';
            }} data-id="element-3435" data-mp="1jlvy9c4o80yo">
                {doubled.map((member, i) => {
                const role = getTranslatedField(member, 'role', isEs);
                const badge = getTranslatedField(member, 'badge', isEs);
                return <a key={`${member.slug}-${i}`} href={`#/member/${member.slug}`} className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col flex-shrink-0 mx-3" style={{
                  width: '300px',
                  minWidth: '300px'
                }} data-id="element-3436" data-mp="lwkmcvzluwrf" data-mpi={i}>
                      {/* Image Container */}
                      <div className="relative h-56 overflow-hidden bg-gray-100" data-id="element-3437" data-mp="37h14u1pv0wca">
                        <img src={member.photo} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={getMemberPhotoStyle(member.slug, 'card')} data-id="element-3438" data-mp="29xg68n0stbg1" />
                        <div className="absolute inset-0 bg-gradient-to-t from-haa-navy/80 via-haa-navy/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" data-id="element-3439" data-mp="1r5ex8rub84sw" />

                        {/* Badge */}
                        {badge && <div className="absolute top-4 right-4" data-id="element-3440" data-mp="1o1fvhc99qkll">
                            <span className="inline-block px-3 py-1 bg-haa-skyblue text-white font-bebas text-sm tracking-wider rounded-full shadow-sm" data-id="element-3441" data-mp="1k15sm8grwaay">
                              {badge}
                            </span>
                          </div>}
                      </div>

                      {/* Content */}
                      <div className="p-5 flex flex-col flex-grow" data-id="element-3442" data-mp="1132xrd1jnj58">
                        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-1 group-hover:text-haa-blue transition-colors" data-id="element-3443" data-mp="114w38p0f7xbn">
                          {member.name}
                        </h3>
                        <p className="font-nunito text-haa-midblue font-semibold text-xs uppercase tracking-wider mb-1" data-id="element-3444" data-mp="1a5uarha2imyz">
                          {role}
                        </p>
                        {member.company && <p className="font-nunito text-gray-500 text-xs mb-0.5" data-id="element-3445" data-mp="7bf2qjn3ksma">
                            {member.company}
                          </p>}
                        <p className="font-nunito text-gray-400 text-xs mb-3" data-id="element-3446" data-mp="c7vsfi9y3f1h">
                          {formatMemberLocation(member.city, member.state)}
                        </p>

                        <div className="mt-auto pt-3 flex items-center text-haa-blue font-nunito font-bold text-sm group-hover:text-haa-skyblue transition-colors" data-id="element-3447" data-mp="3bo4xvz5lc89">
                          {isEs ? 'Ver Perfil' : 'View Profile'}
                          <ArrowRightIcon className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-3448" data-mp="h9l1wthirkng" />
                        </div>
                      </div>
                    </a>;
              })}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}